import {
  Leaf, Sprout, Truck, Award, ShieldCheck, Heart, Mail,
  Star, Zap, Target, BookOpen, Users, Settings, Gift,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface ProcessStepsProps {
  section: any;
}

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Leaf, Sprout, Truck, Award, ShieldCheck, Heart, Mail,
  Star, Zap, Target, BookOpen, Users, Settings, Gift,
};

export default function ProcessSteps({ section }: ProcessStepsProps) {
  const steps: any[] = section.content?.steps ?? [];
  const headingId = `process-steps-heading-${section.id}`;

  if (steps.length === 0) return null;

  return (
    <section aria-labelledby={headingId} className="py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          {section.title && (
            <h2
              id={headingId}
              className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
            >
              {section.title}
            </h2>
          )}
          {section.subtitle && (
            <p className="mt-2 text-sage-600 max-w-xl mx-auto">
              {section.subtitle}
            </p>
          )}
        </div>

        {/* Desktop: horizontal timeline */}
        <div className="hidden md:block">
          <div className="relative flex items-start justify-between">
            {/* Connecting line */}
            <div
              className="absolute top-6 left-6 right-6 h-0.5 bg-sage-200"
              aria-hidden="true"
            />

            {steps.map((step: any, i: number) => {
              const IconComponent = ICON_MAP[step.icon];
              return (
                <div
                  key={i}
                  className="relative flex flex-col items-center text-center flex-1 px-4"
                >
                  {/* Numbered circle */}
                  <div
                    className={cn(
                      'relative z-10 flex h-12 w-12 items-center justify-center rounded-full border-2 border-sage-700 bg-surface',
                      'text-sage-700 font-display text-lg font-bold'
                    )}
                  >
                    {IconComponent ? (
                      <IconComponent className="h-5 w-5" />
                    ) : (
                      step.number ?? i + 1
                    )}
                  </div>
                  <h3 className="mt-4 font-display text-base font-semibold text-sage-900">
                    {step.title}
                  </h3>
                  {step.description && (
                    <p className="mt-1 text-sm text-sage-600 max-w-[200px]">
                      {step.description}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile: vertical timeline */}
        <div className="md:hidden">
          <div className="relative space-y-8 pl-8">
            {/* Vertical connecting line */}
            <div
              className="absolute top-0 bottom-0 left-[1.4rem] w-0.5 bg-sage-200"
              aria-hidden="true"
            />

            {steps.map((step: any, i: number) => {
              const IconComponent = ICON_MAP[step.icon];
              return (
                <div key={i} className="relative flex items-start gap-4">
                  {/* Numbered circle */}
                  <div
                    className={cn(
                      'absolute -left-8 flex h-10 w-10 items-center justify-center rounded-full border-2 border-sage-700 bg-surface',
                      'text-sage-700 font-display text-sm font-bold shrink-0'
                    )}
                  >
                    {IconComponent ? (
                      <IconComponent className="h-4 w-4" />
                    ) : (
                      step.number ?? i + 1
                    )}
                  </div>
                  <div className="pt-1">
                    <h3 className="font-display text-base font-semibold text-sage-900">
                      {step.title}
                    </h3>
                    {step.description && (
                      <p className="mt-1 text-sm text-sage-600">
                        {step.description}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
